import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import {
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { entraEnabled, loginRequest, msalConfig } from './msalConfig';
import { siftApiClient } from '../api/siftApiClient';

// Exported so tests/main can reference the instance if needed.
// eslint-disable-next-line react-refresh/only-export-components
export const msalInstance = new PublicClientApplication(msalConfig);

// Module-level guard: prevents double-initialization when React StrictMode
// runs effects twice in development. MSAL v4 throws "initialize_called" if
// initialize() is called more than once on the same instance.
let _msalInitialized = false;

export interface EntraUser {
  account: AccountInfo;
  accessToken: string;
  email: string;
  displayName: string;
  username: string;
  roles: string[];
}

interface AuthContextValue {
  user: EntraUser | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<EntraUser | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const LEGACY_AUTH: AuthContextValue = {
  user: null,
  loading: false,
  login: async () => undefined,
  logout: async () => undefined,
  refreshSession: async () => null,
};

// Same margin MSAL applies to access tokens (tokenRenewalOffsetSeconds).
const ID_TOKEN_RENEWAL_OFFSET_MS = 5 * 60 * 1000;

// MSAL's silent cache check looks only at the ACCESS token's expiry (60-90 min), so it
// can return an ID token (our bearer, 60 min) that has already lapsed.
function idTokenExpired(result: AuthenticationResult): boolean {
  const exp = (result.idTokenClaims as { exp?: unknown } | undefined)?.exp;
  return typeof exp === 'number' && exp * 1000 <= Date.now() + ID_TOKEN_RENEWAL_OFFSET_MS;
}

// eslint-disable-next-line react-refresh/only-export-components
export async function acquireAccessToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
  forceRefresh = false,
): Promise<AuthenticationResult | null> {
  const request = { ...loginRequest, account, forceRefresh };
  try {
    const result = await instance.acquireTokenSilent(request);
    if (!forceRefresh && idTokenExpired(result)) {
      return await instance.acquireTokenSilent({ ...request, forceRefresh: true });
    }
    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const needsInteraction =
      error instanceof InteractionRequiredAuthError ||
      message.includes('interaction_required') ||
      message.includes('iframe') ||
      message.includes('monitor_window_timeout');

    if (!needsInteraction) throw error;
    await instance.acquireTokenRedirect(request);
    return null;
  }
}

function mapSession(account: AccountInfo, accessToken: string, me: { email?: string; display_name?: string; role?: string } | null): EntraUser {
  return {
    account,
    accessToken,
    email: me?.email ?? account.username,
    displayName: me?.display_name ?? account.name ?? account.username,
    username: account.username,
    roles: me?.role ? me.role.split(',').map((r) => r.trim()).filter(Boolean) : [],
  };
}

function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<EntraUser | null>(null);

  const refreshSession = useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      siftApiClient.clearAccessToken();
      setUser(null);
      return null;
    }

    instance.setActiveAccount(account);
    const tokenResult = await acquireAccessToken(instance, account);
    if (!tokenResult) return null;

    // Prefer ID token when no API scope is configured — the API scope
    // (VITE_SIFT_AZURE_API_SCOPE) must be registered in the Entra App Registration
    // under "Expose an API" before an access token with the correct audience can be
    // issued. Until that is done, fall back to the ID token whose aud = client_id,
    // which the backend accepts as a valid audience.
    const bearer = tokenResult.idToken || tokenResult.accessToken;
    siftApiClient.setAccessToken(bearer || null);
    // The profile lookup answers "what may you do", not "who are you". Losing it
    // (backend cold start, 5xx, network) must not discard a valid Entra session —
    // App re-fetches the profile and decides what to show. Dropping the session
    // here sent users to a login screen that could not fix a backend outage.
    let me: { email?: string; display_name?: string; role?: string } | null = null;
    try {
      me = await siftApiClient.me(bearer || null);
    } catch (error) {
      console.warn('[auth] profile lookup failed; keeping the Entra session', error);
    }
    const session = mapSession(account, bearer || '', me);
    setUser(session);
    return session;
  }, [accounts, instance]);

  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const result = event.payload as AuthenticationResult;
        if (result.account) instance.setActiveAccount(result.account);
      }
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  // §5.2 let the API client re-mint an expired session on a 401 instead of
  // failing the request. Returns the fresh bearer, or null to let the 401 stand.
  useEffect(() => {
    siftApiClient.setTokenRefresher(async () => {
      const account = instance.getActiveAccount() ?? accounts[0];
      if (!account) return null;
      // The server just rejected the cached token, so do not trust the cache again.
      const result = await acquireAccessToken(instance, account, true);
      // A null result means MSAL has redirected for interaction; there is no
      // token to retry with and the page is going away regardless.
      if (!result) return null;
      return result.idToken || result.accessToken || null;
    });
    return () => siftApiClient.setTokenRefresher(null);
  }, [instance, accounts]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        // initialize() was already called in main.tsx before first render;
        // only handle the redirect promise and restore the session here.
        await instance.handleRedirectPromise();
        await refreshSession();
      } catch (error) {
        // Without this the rejection is unhandled and `loading` clears with the
        // session half-restored, which renders as "signed out".
        console.error('[auth] session restore failed', error);
      } finally {
        setLoading(false);
      }
    };
    void init();
  }, [instance, refreshSession]);

  const login = useCallback(async () => {
    console.log('[MSAL] loginRedirect called with request:', loginRequest);
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    setUser(null);
    siftApiClient.clearAccessToken();
    await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [instance]);

  const value = useMemo<AuthContextValue>(() => ({ user, loading, login, logout, refreshSession }), [user, loading, login, logout, refreshSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [msalReady, setMsalReady] = useState(!entraEnabled);
  const [msalError, setMsalError] = useState<string | null>(null);

  useEffect(() => {
    if (!entraEnabled) return;

    // Guard against StrictMode double-invocation: initialize() must only be
    // called once per MSAL instance — calling it twice throws "initialize_called".
    if (_msalInitialized) {
      setMsalReady(true);
      return;
    }
    _msalInitialized = true;

    msalInstance.initialize()
      .then(() => {
        console.log('[MSAL] initialize() succeeded');
        setMsalReady(true);
      })
      .catch((err: unknown) => {
        _msalInitialized = false; // allow retry
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[MSAL] initialize failed:', msg);
        setMsalError(msg);
      });
  }, []);

  if (!entraEnabled) {
    return <AuthContext.Provider value={LEGACY_AUTH}>{children}</AuthContext.Provider>;
  }

  if (msalError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: 'red', fontFamily: 'sans-serif', padding: '2rem' }}>
          <h2>SSO initialization failed</h2>
          <pre style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{msalError}</pre>
        </div>
      </div>
    );
  }

  if (!msalReady) {
    // Show a loading screen (with inline styles so it's visible even before CSS loads)
    // instead of null — returning null leaves the page completely blank.
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f8f9ff' }}>
        <div style={{ textAlign: 'center', fontFamily: 'sans-serif' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>SIFT</h1>
          <p style={{ color: '#666' }}>Initializing SSO…</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}



